import { DocumentSearchDao } from '@rock/db-admin';
import { newDocumentSearch, offerStoragePath } from '@rock/manuals';
import {
    type DocumentSearch,
    type ModelDocumentCandidate,
    type ModelDocumentId,
    ModelDocumentIdSchema,
    type ModelId,
} from '@rock/types';
import { isHttpUrl } from '@rock/util';

const searchDao = new DocumentSearchDao();

/** Uploads a file to a Storage path and resolves its download URL (`useStorageUpload().uploadFile`). */
export type UploadFile = (file: File, storagePath: string, metadata?: { contentType?: string }) => Promise<string>;

export interface OfferOptions {
    /** Filed as a newer revision of this document: its candidate reviews as that revision. */
    replaces?: ModelDocumentId;
    /** Fetched full-size, past the staging cap — an admin's explicit "bring it in". */
    allowOversize?: boolean;
}

/** Ask the worker to search for one model's documents again, with no offer: "run again with current information". */
export async function requestDocumentSearch(modelId: ModelId): Promise<void> {
    await fileSearch(modelId, {});
}

/**
 * Offer a FILE to one model's pipeline — the same act as offering a URL, minus the finding. The bytes are
 * parked under the model's `offers/` prefix (NOT `candidates/`, which the worker owns: overwriting a staged
 * candidate's object would rotate its download token and 403 the preview), then a search's `offeredUrl`
 * points at them and the worker pins, verifies and stages it as a candidate for review. `allowOversize`
 * keeps an admin's chosen file free of the staging cap, the same explicit act as "Bring it in".
 */
export async function offerDocumentFile(
    modelId: ModelId,
    file: File,
    uploadFile: UploadFile,
    opts: OfferOptions = {},
): Promise<void> {
    const bytes = await file.arrayBuffer();
    // Rejected HERE: `accept` is only a picker hint, and the worker's own check is a plain fetch miss that
    // would burn every backed-off attempt re-downloading the file before giving up.
    if (!isPdfBytes(bytes)) throw new Error('That file is not a PDF');
    const hash = ModelDocumentIdSchema.parse(await contentHashId(bytes));
    const offeredUrl = await uploadFile(file, offerStoragePath(modelId, hash), {
        contentType: file.type || 'application/pdf',
    });
    await fileSearch(modelId, { ...opts, offeredUrl, allowOversize: true });
}

/**
 * Offer a URL to one model's pipeline: the worker pins it before any discovery spend. Refuses what the
 * worker's `isHttpUrl` would drop — the search would run as a plain hunt, and the offer never arrive.
 */
export async function offerDocumentUrl(modelId: ModelId, offeredUrl: string, opts: OfferOptions = {}): Promise<void> {
    if (!isHttpUrl(offeredUrl)) throw new Error('Enter a full http(s) link to the document');
    await fileSearch(modelId, { ...opts, offeredUrl });
}

/** Which candidates came from an offer filed as a newer revision, keyed by candidate id. */
export function offeredRevisions(
    searches: DocumentSearch[],
    candidates: ModelDocumentCandidate[],
): Partial<Record<ModelDocumentId, ModelDocumentId>> {
    const revisions: Partial<Record<ModelDocumentId, ModelDocumentId>> = {};
    for (const search of searches) {
        if (!search.replaces || !search.offeredUrl) continue;
        const offered = candidates.find((candidate) => candidate.urls.includes(search.offeredUrl!));
        if (offered && offered.id !== search.replaces) revisions[offered.id] = search.replaces;
    }
    return revisions;
}

async function fileSearch(modelId: ModelId, opts: OfferOptions & { offeredUrl?: string }): Promise<void> {
    const search = newDocumentSearch(searchDao.newSearchId(modelId), modelId, new Date().toISOString(), opts);
    await searchDao.setDocumentSearch(search);
}

/** sha-256 prefix (12 hex) of a file's bytes — the same content identity the worker stamps on candidates. */
async function contentHashId(bytes: ArrayBuffer): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, 12);
}

/** The `%PDF-` magic bytes — the same signature check the worker's `isPdf` runs on what it fetches. */
function isPdfBytes(bytes: ArrayBuffer): boolean {
    const header = new Uint8Array(bytes, 0, Math.min(5, bytes.byteLength));
    return new TextDecoder('latin1').decode(header) === '%PDF-';
}
